import { useEffect, useState } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, Users, MessageSquare, BookOpen, Activity, Loader2 } from 'lucide-react';
import { getAdvancedAnalytics } from '../../services/analytics';

const COLORS = ['#22c55e', '#eab308', '#ef4444', '#3b82f6'];

function StatCard({ icon, label, value, color }) {
    const bg = {
        blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        orange: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    };
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className={`w-10 h-10 rounded-xl ${bg[color]} flex items-center justify-center text-lg mb-3`}>{icon}</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
        </div>
    );
}

export default function AdvancedAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getAdvancedAnalytics()
            .then((res) => setData(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    const difficultyData = (data?.difficulty_stats || []).map((item) => ({
        name: item.tag === 'easy' ? 'Oson' : item.tag === 'hard' ? 'Murakkab' : "O'rta",
        total: item.total,
        correct: item.correct,
        incorrect: item.total - item.correct,
    }));

    const activityData = (data?.activity_by_day || []).map((item) => ({
        date: item.date,
        count: item.count,
    }));

    const cognitiveTotal = (data?.cognitive?.total || 0);
    const cognitiveCorrect = (data?.cognitive?.correct || 0);
    const cognitiveIncorrect = cognitiveTotal - cognitiveCorrect;
    const pieData = cognitiveTotal > 0
        ? [
            { name: "To'g'ri", value: cognitiveCorrect },
            { name: "Xato", value: cognitiveIncorrect },
        ]
        : [];

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
                <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6" /> Kengaytirilgan o'quv analitikasi
                </h1>
                <p className="text-indigo-100">Talabalar faolligi, kognitiv ko'rsatkichlar va motivatsion indikatorlar</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={<Activity className="w-5 h-5" />} label="Jami hodisalar" value={data?.total_events || 0} color="blue" />
                <StatCard icon={<MessageSquare className="w-5 h-5" />} label="Chat murojaatlari" value={data?.chat_events || 0} color="purple" />
                <StatCard icon={<BookOpen className="w-5 h-5" />} label="Test javoblari" value={data?.test_answer_events || 0} color="orange" />
                <StatCard icon={<Users className="w-5 h-5" />} label="Tugatilgan darslar" value={data?.lesson_complete_events || 0} color="green" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Qiyinlik darajasi bo'yicha natijalar</h2>
                    {difficultyData.length > 0 ? (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={difficultyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="correct" name="To'g'ri" fill="#22c55e" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="incorrect" name="Xato" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-8">Hali ma'lumot yo'q</p>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Kognitiv indikator (to'g'ri/xato)</h2>
                    {pieData.length > 0 ? (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-8">Hali ma'lumot yo'q</p>
                    )}
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Kunlik faollik</h2>
                {activityData.length > 0 ? (
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={activityData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-8">Hali ma'lumot yo'q</p>
                )}
            </div>
        </div>
    );
}
